<template>
	<div class="container stats-page">
		<section class="page-hero">
			<div>
				<span>LINK ANALYTICS</span>
				<h2>访问链路统计</h2>
				<p>按入口域名和中间域名查看 PV、UV 与访问分布。</p>
			</div>
		</section>
		<el-card shadow="never" class="stats-card">
			<el-tabs v-model="tab" @input="changeTab"><el-tab-pane label="访问链路" name="chain"/><el-tab-pane label="目标地址" name="target"/></el-tabs>
			<div v-if="tab === 'target'" class="target-panel">
				<div class="refresh-note"><i class="el-icon-time" />单日 UV 按当日 IP 去重；汇总 UV 为每日 UV 累加，独立 IP 数按全部历史跨天去重。PV/UV 每 5 分钟更新，汇总独立 IP 每天更新一次。</div>
				<div class="target-toolbar">
					<div class="target-title">
						<span>目标地址分析</span><small>{{ targetPeriodText }}的访问汇总</small>
					</div>
					<div class="target-filters">
						<el-select v-model="groupId" clearable size="small" placeholder="全部分组" @change="loadTargets"><el-option v-for="item in groups" :key="item.id" :label="item.name" :value="item.id"/></el-select>
						<el-date-picker
							v-model="targetDate"
							size="small"
							type="date"
							value-format="timestamp"
							placeholder="选择日期"
							:picker-options="pickerOptions"
							@change="
								targetPeriod = 'custom';
								loadTargets();
							"
						/>
						<el-button size="small" :type="isTargetToday ? 'primary' : 'default'" @click="setToday('target')">今天</el-button>
						<el-button
							size="small"
							:type="targetPeriod === 'summary' ? 'primary' : 'default'"
							@click="
								targetPeriod = 'summary';
								loadTargets();
							"
							>汇总</el-button
						>
						<el-button size="small" icon="el-icon-refresh" :loading="targetRefreshing" :disabled="refreshCooldown > 0" @click="refreshStatistics('target')">{{ refreshButtonText }}</el-button>
					</div>
				</div>
				<div class="target-summary" :class="{ 'target-summary-four': targetPeriod === 'summary' }">
					<div>
						<small>目标地址</small><strong>{{ targetRows.length }}</strong>
					</div>
					<div>
						<small>总 PV</small><strong>{{ targetSummary.pv }}</strong>
					</div>
					<div>
						<small>UV 合计（每日累加）</small><strong>{{ targetSummary.uv }}</strong>
					</div>
					<div v-if="targetPeriod === 'summary'">
						<small>独立 IP 数</small><strong>{{ targetIndependentIP }}</strong>
					</div>
				</div>
				<el-table v-loading="targetLoading" :data="targetRows" class="target-table target-list-table sublevel-table" empty-text="当前周期暂无目标地址访问数据" max-height="520">
					<el-table-column label="目标地址" prop="target_url" min-width="300" show-overflow-tooltip
						><template slot-scope="{ row }"
							><span class="target-url"><i class="el-icon-link" />{{ row.target_url }}</span></template
						></el-table-column
					>
					<el-table-column label="归属分组" min-width="150"
						><template slot-scope="{ row }"
							><el-tag class="group-tag" effect="plain" :class="{ 'is-unassigned': !row.domain_group_id }" :style="groupTagStyle(row.domain_group_id)"><i class="el-icon-collection-tag" />{{ groupName(row.domain_group_id) }}</el-tag></template
						></el-table-column
					>
					<el-table-column :label="'PV (' + targetPeriodText + ')'" prop="pv" min-width="120" align="right"
						><template slot-scope="{ row }"
							><strong class="table-number">{{ row.pv }}</strong></template
						></el-table-column
					>
					<el-table-column :label="'UV (' + targetUVText + ')'" prop="uv" min-width="120" align="right"
						><template slot-scope="{ row }"
							><strong class="table-number muted">{{ row.uv }}</strong></template
						></el-table-column
					>
					<el-table-column v-if="targetPeriod === 'summary'" label="独立 IP 数" prop="independent_ip" min-width="120" align="right"
						><template slot-scope="{ row }"
							><strong class="table-number independent">{{ row.independent_ip || 0 }}</strong></template
						></el-table-column
					>
				</el-table>
			</div>
			<div v-show="tab === 'chain'" class="target-toolbar chain-toolbar">
				<div class="refresh-note"><i class="el-icon-time" />单日 UV 按当日 IP 去重；汇总 UV 为每日 UV 累加，独立 IP 数按全部历史跨天去重。PV/UV 每 5 分钟更新，汇总独立 IP 每天更新一次。</div>
				<div class="target-title">
					<span>访问链路分析</span><small>{{ periodText }}的访问汇总，点击入口域名查看中间域名流量</small>
				</div>
				<div class="target-filters">
					<el-select v-model="groupId" clearable size="small" placeholder="全部分组" @change="changeGroup"><el-option v-for="item in groups" :key="item.id" :label="item.name" :value="item.id"/></el-select>
					<el-date-picker
						v-model="chainDate"
						size="small"
						type="date"
						value-format="timestamp"
						placeholder="选择日期"
						:picker-options="pickerOptions"
						@change="
							chainPeriod = 'custom';
							load();
						"
					/>
					<el-button size="small" :type="isChainToday ? 'primary' : 'default'" @click="setToday('chain')">今天</el-button>
					<el-button
						size="small"
						:type="chainPeriod === 'summary' ? 'primary' : 'default'"
						@click="
							chainPeriod = 'summary';
							load();
						"
						>汇总</el-button
					>
					<el-button size="small" icon="el-icon-refresh" :loading="refreshing" :disabled="refreshCooldown > 0" @click="refreshStatistics('chain')">{{ refreshButtonText }}</el-button>
				</div>
			</div>
			<div v-show="tab === 'chain'" class="target-summary" :class="{ 'chain-summary': chainPeriod === 'summary' }">
				<div>
					<small>{{ periodText }} PV</small><strong>{{ chainSummary.pv || 0 }}</strong>
				</div>
				<div>
					<small>UV 合计（每日累加）</small><strong>{{ chainSummary.uv || 0 }}</strong>
				</div>
				<div v-if="chainPeriod === 'summary'">
					<small>独立 IP 数</small><strong>{{ chainSummary.independent_ip || 0 }}</strong>
				</div>
				<div>
					<small>入口域名</small><strong>{{ allEntryRows.length }}</strong>
				</div>
			</div>
			<div v-show="tab === 'chain'" class="breadcrumb">
				<span :class="{ active: level === 'entry' }" @click="backToEntries">全部入口域名</span>
				<template v-if="selectedEntry">
					<i class="el-icon-arrow-right" /><span :class="{ active: level === 'backend' }">{{ selectedEntryName }}</span>
				</template>
				<el-button v-if="level !== 'entry'" type="text" icon="el-icon-back" class="back-button" @click="backToEntries">返回入口域名</el-button>
			</div>
			<el-table v-if="tab === 'chain' && level === 'entry'" v-loading="loading" :data="allEntryRows" class="chain-entry-table sublevel-table" empty-text="暂无入口域名" max-height="520" @row-click="selectEntry">
				<el-table-column label="入口域名" min-width="180"
					><template slot-scope="{ row }"
						><span class="domain-cell"><i class="el-icon-position" />{{ row.entry_domain }}</span></template
					></el-table-column
				>
				<el-table-column label="归属分组" min-width="160"
					><template slot-scope="{ row }"
						><el-tag class="group-tag" effect="plain" :class="{ 'is-unassigned': !row.domain_group_id }" :style="groupTagStyle(row.domain_group_id)"><i class="el-icon-collection-tag" />{{ groupName(row.domain_group_id) }}</el-tag></template
					></el-table-column
				>
				<el-table-column label="中间域名" prop="backend_count" min-width="100" align="center" /><el-table-column :label="'PV (' + periodText + ')'" prop="pv" min-width="100" align="right" /><el-table-column :label="'UV (' + chainUVText + ')'" prop="uv" min-width="110" align="right" /><el-table-column v-if="chainPeriod === 'summary'" label="独立 IP" prop="independent_ip" min-width="100" align="right" /><el-table-column label="操作" min-width="90" align="center"
					><template slot-scope="{ row }"><el-button type="text" @click.stop="selectEntry(row)">查看下级</el-button></template></el-table-column
				>
			</el-table>
			<el-table v-else-if="tab === 'chain' && level === 'backend'" v-loading="loading" :data="backendRows" class="sublevel-table backend-table" empty-text="该入口域名暂无中间域名流量" max-height="520">
				<el-table-column label="中间域名" min-width="140"
					><template slot-scope="{ row }"
						><span class="domain-cell backend"><i class="el-icon-refresh" />{{ row.backend_domain }}</span></template
					></el-table-column
				>
				<el-table-column :label="'PV (' + periodText + ')'" prop="pv" min-width="110" align="right"
					><template slot-scope="{ row }"
						><strong class="table-number">{{ row.pv }}</strong></template
					></el-table-column
				><el-table-column :label="'UV (' + chainUVText + ')'" prop="uv" min-width="110" align="right"
					><template slot-scope="{ row }"
						><strong class="table-number muted">{{ row.uv }}</strong></template
					></el-table-column
				>
			</el-table>
		</el-card>
	</div>
</template>
<script>
	import dayjs from 'dayjs';
	import { groupTagStyle } from '@/utils/group-tag';

	export default {
		name: 'ShortLinkStats',
		data() {
			return {
				loading: false,
				targetLoading: false,
				refreshing: false,
				targetRefreshing: false,
				refreshCooldown: 0,
				refreshTimer: null,
				refreshPollTimer: null,
				statisticsVersion: '',
				loadId: 0,
				targetLoadId: 0,
				groups: [],
				groupId: '',
				metaGroupId: null,
				tab: 'chain',
				chainPeriod: 'custom',
				chainDate: Date.now(),
				targetPeriod: 'custom',
				targetDate: Date.now(),
				targetRows: [],
				targetIndependentIP: 0,
				selectedEntry: '',
				entryDomains: [],
				chainStats: {},
				pickerOptions: { disabledDate: (time) => time.getTime() > Date.now() }
			};
		},
		computed: {
			groupNames() {
				return this.groups.reduce((names, group) => {
					names[group.id] = group.name;
					return names;
				}, {});
			},
			selectedChain() {
				return this.chainStats[this.chainPeriod] || {};
			},
			chainSummary() {
				if (!this.selectedEntry) return this.selectedChain.summary || {};
				return (this.selectedChain.entry_list || []).find((item) => this.entryKey(item) === this.selectedEntry) || {};
			},
			selectedEntryName() {
				return (this.chainSummary || {}).entry_domain || '';
			},
			periodText() {
				return this.chainPeriod === 'summary' ? '累加值' : this.isChainToday ? '今天' : dayjs(this.chainDate).format('YYYY-MM-DD');
			},
			targetPeriodText() {
				return this.targetPeriod === 'summary' ? '累加值' : this.isTargetToday ? '今天' : dayjs(this.targetDate).format('YYYY-MM-DD');
			},
			targetUVText() {
				return this.targetPeriod === 'summary' ? '累加值' : this.targetPeriodText;
			},
			chainUVText() {
				return this.chainPeriod === 'summary' ? '累加值' : this.periodText;
			},
			isTargetToday() {
				return this.targetPeriod === 'custom' && this.targetDate && dayjs(this.targetDate).isSame(dayjs(), 'day');
			},
			isChainToday() {
				return this.chainPeriod === 'custom' && this.chainDate && dayjs(this.chainDate).isSame(dayjs(), 'day');
			},
			targetSummary() {
				return this.targetRows.reduce((summary, item) => ({ pv: summary.pv + Number(item.pv || 0), uv: summary.uv + Number(item.uv || 0) }), { pv: 0, uv: 0 });
			},
			refreshButtonText() {
				return this.refreshCooldown > 0 ? `刷新 (${this.refreshCooldown}s)` : '刷新';
			},
			level() {
				return this.selectedEntry ? 'backend' : 'entry';
			},
			allEntryRows() {
				const stats = new Map((this.selectedChain.entry_list || []).map((item) => [this.entryKey(item), item]));
				const rows = this.entryDomains.map((domain) => this.entryRow(domain.content, domain.domain_group_id, stats.get(`${domain.domain_group_id}:${domain.content}`)));
				const keys = new Set(rows.map((row) => this.entryKey(row)));
				stats.forEach((stat, key) => {
					if (!keys.has(key)) rows.push(this.entryRow(stat.entry_domain, stat.domain_group_id, stat));
				});
				return rows.sort((a, b) => b.pv - a.pv || a.entry_domain.localeCompare(b.entry_domain));
			},
			backendRows() {
				return (this.selectedChain.backend_list || []).filter((row) => this.entryKey(row) === this.selectedEntry);
			}
		},
		created() {
			this.request.get('/domain-group', { params: { state: 2 } }).then((data) => {
				this.groups = data.list || [];
				this.groupId = '';
				this.load();
			});
		},
		beforeDestroy() {
			clearInterval(this.refreshTimer);
			clearTimeout(this.refreshPollTimer);
		},
		methods: {
			groupTagStyle,
			startRefreshCooldown(seconds) {
				this.refreshCooldown = Math.max(0, Number(seconds || 0));
				clearInterval(this.refreshTimer);
				if (this.refreshCooldown <= 0) return;
				this.refreshTimer = setInterval(() => {
					if (--this.refreshCooldown <= 0) clearInterval(this.refreshTimer);
				}, 1000);
			},
			refreshStatistics(scope) {
				const key = scope === 'target' ? 'targetRefreshing' : 'refreshing';
				if (this[key] || this.refreshCooldown > 0) return;
				this[key] = true;
				let version = this.statisticsVersion;
				let attempts = 0;
				const check = () => {
					(scope === 'target' ? this.loadTargets() : this.load())
						.catch(() => {})
						.finally(() => {
							if (this.statisticsVersion !== version || attempts++ >= 20) {
								this[key] = false;
								return;
							}
							this.refreshPollTimer = setTimeout(check, 1500);
						});
				};
				this.request
					.post('/common-statistics-refresh')
					.then((data) => {
						version = String(data.version || version);
						this.startRefreshCooldown(data.retry_after);
						if (!data.queued) {
							this[key] = false;
							return;
						}
						check();
					})
					.catch(() => {
						this[key] = false;
					});
			},
			setToday(scope) {
				const date = Date.now();
				if (scope === 'target') {
					this.targetDate = date;
					this.targetPeriod = 'custom';
					this.loadTargets();
					return;
				}
				this.chainDate = date;
				this.chainPeriod = 'custom';
				this.load();
			},
			changeTab(tab) {
				if (tab === 'target') this.loadTargets();
				else this.load();
			},
			groupName(id) {
				return this.groupNames[id] || '未分组';
			},
			loadTargets() {
				const loadId = ++this.targetLoadId;
				this.targetLoading = true;
				return this.request
					.get('/common-statistics', {
						params: {
							scope: 'target',
							domain_group_id: this.groupId,
							period: this.targetPeriod,
							date: this.targetPeriod === 'custom' ? dayjs(this.targetDate).format('YYYY-MM-DD') : undefined
						}
					})
					.then((data) => {
						if (loadId !== this.targetLoadId) return;
						this.statisticsVersion = String(data.version || '');
						const rows = (data.list || []).map((row) => ({
							...row,
							independent_ip: Number(row.independent_ip || (this.targetPeriod === 'custom' ? row.uv : 0))
						}));
						this.targetRows = rows.sort((a, b) => Number(b.pv || 0) - Number(a.pv || 0));
						this.targetIndependentIP = Number(data.independent_ip || 0);
					})
					.finally(() => {
						if (loadId === this.targetLoadId) this.targetLoading = false;
					});
			},
			entryRow(domain, groupId, stat) {
				return { entry_domain: domain, domain_group_id: groupId, backend_count: Number((stat || {}).backend_count || 0), pv: Number((stat || {}).pv || 0), uv: Number((stat || {}).uv || 0), independent_ip: Number((stat || {}).independent_ip || 0) };
			},
			entryKey(row) {
				return `${row.domain_group_id || 0}:${row.entry_domain}`;
			},
			selectEntry(row) {
				this.selectedEntry = this.entryKey(row);
			},
			backToEntries() {
				this.selectedEntry = '';
			},
			changeGroup() {
				this.backToEntries();
				this.load();
			},
			load() {
				const loadId = ++this.loadId;
				this.loading = true;
				const requests = [this.request.get('/common-statistics', { params: { scope: 'chain', domain_group_id: this.groupId, period: this.chainPeriod, level: 'entry', date: this.chainPeriod === 'custom' ? dayjs(this.chainDate).format('YYYY-MM-DD') : undefined } })];
				if (this.metaGroupId !== this.groupId) {
					requests.push(this.request.get('/domain', { params: { page: 1, pagesize: -1, domain_type: 1, domain_group_id: this.groupId } }));
				}
				return Promise.all(requests)
					.then((responses) => {
						if (loadId !== this.loadId) return;
						this.statisticsVersion = String(responses[0].version || '');
						this.chainStats = responses[0].chain_stats || {};
						if (responses.length > 1) {
							this.entryDomains = responses[1].list || [];
							this.metaGroupId = this.groupId;
						}
					})
					.finally(() => {
						if (loadId === this.loadId) this.loading = false;
					});
			}
		}
	};
</script>
<style scoped>
	.stats-page {
		padding-top: 18px;
	}
	.stats-card {
		border: 1px solid #e8eef4;
		border-radius: 14px;
		box-shadow: 0 10px 28px rgba(38, 68, 96, 0.07);
	}
	.stats-card /deep/ .el-card__body {
		padding: 20px;
	}
	.stats-card /deep/ .el-tabs__header {
		margin-bottom: 18px;
	}
	.page-hero {
		box-sizing: border-box;
		height: 96px;
		margin-bottom: 18px;
		padding: 14px 24px;
		color: #fff;
		border-radius: 14px;
		background: linear-gradient(120deg, #315779, #4f8f9b);
		box-shadow: 0 12px 26px rgba(49, 87, 121, 0.16);
	}
	.page-hero span {
		color: rgba(255, 255, 255, 0.68);
		font-size: 10px;
		letter-spacing: 1.4px;
	}
	.page-hero h2 {
		margin: 2px 0 4px;
		font-size: 20px;
	}
	.page-hero p {
		margin: 0;
		color: rgba(255, 255, 255, 0.82);
		font-size: 12px;
	}
	.filters {
		display: flex;
		gap: 10px;
		margin-bottom: 18px;
	}
	.filters .el-select {
		width: 220px;
	}
	.target-panel {
		padding-top: 8px;
	}
	.target-toolbar {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 18px;
		flex-wrap: wrap;
		margin-bottom: 18px;
		padding: 4px 2px 16px;
		border-bottom: 1px solid #edf1f5;
	}
	.refresh-note {
		flex: 0 0 100%;
		padding: 8px 11px;
		border-radius: 7px;
		color: #7b8a9b;
		background: #f7fafc;
		font-size: 12px;
	}
	.refresh-note i {
		margin-right: 6px;
		color: #4d91a5;
	}
	.chain-toolbar {
		margin-top: 8px;
	}
	.target-title {
		display: flex;
		flex-direction: column;
		gap: 5px;
		color: #30465f;
	}
	.target-title span {
		font-size: 16px;
		font-weight: 600;
	}
	.target-title small {
		color: #9aa8b6;
		font-size: 12px;
	}
	.target-filters {
		display: flex;
		align-items: center;
		gap: 10px;
		flex-wrap: wrap;
		justify-content: flex-end;
	}
	.target-filters .el-select {
		width: 180px;
	}
	.target-summary {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 12px;
		margin-bottom: 18px;
	}
	.target-summary-four {
		grid-template-columns: repeat(4, minmax(0, 1fr));
	}
	.target-summary div {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: space-between;
		overflow: hidden;
		padding: 13px 16px;
		border: 1px solid #e7edf2;
		border-radius: 10px;
		background: linear-gradient(135deg, #fbfcfe, #f5f9fc);
	}
	.target-summary div::before {
		position: absolute;
		top: 0;
		left: 0;
		width: 3px;
		height: 100%;
		background: #5a9cab;
		content: '';
	}
	.target-summary small {
		color: #8493a2;
	}
	.target-summary strong {
		color: #315779;
		font-size: 20px;
	}
	.target-table .target-url {
		color: #405d78;
	}
	.target-table .target-url i {
		margin-right: 7px;
		color: #65a5ad;
	}
	.sublevel-table {
		border-color: #e3ebf1;
		box-shadow: 0 5px 16px rgba(46, 82, 108, 0.04);
	}
	.sublevel-table /deep/ th {
		color: #718397;
		background: #f7fafc;
		font-size: 15px;
		font-weight: 600;
	}
	.sublevel-table /deep/ td {
		height: 54px;
	}
	.target-list-table /deep/ td {
		height: 58px;
	}
	.target-list-table /deep/ th:first-child .cell,
	.target-list-table /deep/ td:first-child .cell,
	.target-list-table /deep/ th:nth-child(2) .cell,
	.target-list-table /deep/ td:nth-child(2) .cell {
		text-align: left;
	}
	.sublevel-table /deep/ .cell {
		padding-right: 16px;
		padding-left: 16px;
	}
	.backend-table /deep/ .el-table__row {
		cursor: pointer;
	}
	.sublevel-table /deep/ .el-table__row:hover > td {
		background: #f2f9fb;
	}
	.chain-entry-table /deep/ .el-table__row {
		cursor: pointer;
	}
	.chain-entry-table /deep/ th:first-child .cell,
	.chain-entry-table /deep/ td:first-child .cell,
	.chain-entry-table /deep/ th:nth-child(2) .cell,
	.chain-entry-table /deep/ td:nth-child(2) .cell {
		text-align: left;
	}
	.chain-entry-table /deep/ td:nth-child(n + 3):nth-child(-n + 6) .cell {
		color: #587187;
		font-weight: 650;
	}
	.sublevel-table .target-url {
		color: #405d78;
	}
	.sublevel-table .target-url i {
		margin-right: 7px;
		color: #65a5ad;
	}
	.table-number {
		color: #2e7186;
		font-size: 14px;
		font-weight: 700;
	}
	.table-number.muted {
		color: #71879a;
		font-weight: 600;
	}
	.table-number.independent {
		color: #4d7b68;
	}
	.target-filters .el-date-editor,
	.chain-toolbar .el-date-editor {
		width: 145px;
	}
	.chain-summary {
		grid-template-columns: repeat(4, 1fr);
	}
	.breadcrumb {
		display: flex;
		align-items: center;
		min-height: 38px;
		margin-bottom: 12px;
		padding: 0 12px;
		border-radius: 8px;
		color: #8190a2;
		background: #f6f8fb;
		font-size: 13px;
	}
	.breadcrumb span {
		cursor: pointer;
	}
	.back-button {
		margin-left: auto;
		padding-left: 14px;
		border-left: 1px solid #dfe6ee;
	}
	.breadcrumb span:hover,
	.breadcrumb span.active {
		color: #2d718e;
		font-weight: 650;
	}
	.breadcrumb i {
		margin: 0 8px;
		color: #b0bac5;
	}
	.domain-cell {
		display: inline-flex;
		align-items: center;
		color: #2b607d;
		font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
		font-weight: 650;
	}
	.domain-cell i {
		margin-right: 8px;
		color: #4c94ae;
	}
	.domain-cell.backend {
		color: #536f8a;
	}
	.group-tag {
		display: inline-flex;
		align-items: center;
		max-width: 140px;
		height: 26px;
		padding: 0 10px;
		overflow: hidden;
		border-radius: 13px;
		font-weight: 600;
		line-height: 24px;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.group-tag i {
		margin-right: 5px;
		color: inherit;
	}
	.group-tag.is-unassigned {
		color: #84909f !important;
		border-color: #e1e6eb !important;
		background: #f7f8fa !important;
	}
	.share {
		color: #2c7b84;
		font-weight: 700;
	}
	.el-table {
		overflow: hidden;
		border: 1px solid #e8eff5;
		border-radius: 10px;
	}
	@media (max-width: 760px) {
		.stats-card /deep/ .el-card__body {
			padding: 14px;
		}
		.target-summary {
			grid-template-columns: 1fr;
		}
		.target-summary-four {
			grid-template-columns: 1fr;
		}
		.chain-summary {
			grid-template-columns: repeat(2, 1fr);
		}
		.page-hero {
			height: auto;
		}
		.filters {
			flex-wrap: wrap;
		}
		.filters .el-select {
			width: 100%;
		}
		.target-filters {
			justify-content: flex-start;
			width: 100%;
		}
	}
</style>
